/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, gridClasses, Box, Tooltip } from '@mui/material';
import ModalDialog from '../ui/ModalDialog/index';
import { CancelPresentationRounded, DeleteRounded, PersonAddAlt, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './style';
import Datagrid from '../ui/Datagrid';
import columns from './table_config';
import { renderCell } from '../ui/Datagrid/table_config';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import FileUploadDialog from '../ui/FileUploadDialog';
import DeleteConfirmationModal from '../deleteConfirmationModal';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import useUnitConversion from '../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../shared/config/hydraulics_constants';
import { APP_ACTION_TYPES, HYDRAULICS_MAX_FILE_SIZE, PERMISSION_DIALOG_SOURCES, TOOLTIPS } from '../../utils/constants';
import { addNewPWDRow, createPWD, deletePWDRow, importWellsPWDCSVFIle, listPWD, updatePWD } from '../../store/actions/wellsPWD';
import PermissionsModal from '../permissions';
import { checkHydraulicsAdmin, checkPermissions } from '../../utils/checkPermissions';
import CreatedByInfoDialog from '../ui/CreatedByInfoDialog';
import { REDUX_ACTIONS } from '../../shared/config/constants';
const PWDModal = ({
  onCloseClick,
  wellsData,
  isOpen
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const pwd = useSelector((state) => state.wellsPWD);
  const apiRef = useGridApiRef();
  const [nonEditableFields, setNonEditableFields] = useState(false);
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);
  const disableAction = properties.some(item => item?.isDirty === true || item?.isNewRow);
  const user = useSelector((state) => state.currentUser);
  const [permissions, setPermissions] = useState({
    open: false,
    data: {}
  });
  const isHydraulicsAdmin = checkHydraulicsAdmin(
    user?.type,
    user?.accessLevels
  );

  useEffect(() => {
    if (!pwd.loading) {
      dispatch(listPWD(wellsData?.row?._id, onCloseClick));
    }
    return () => {
      dispatch({
        type: REDUX_ACTIONS.WELLS_PWD_RESET
      });
    };
  }, []);

  useEffect(() => {
    if (pwd?.data) {
      setProperties(pwd?.data);
    }
  }, [pwd?.data]);

  useEffect(() => {
    if (properties[0]?.md === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'md', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'md');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddPWD = () => {
    dispatch(addNewPWDRow());
  };

  const removePWD = (row) => {
    if (row?.isNewRow) {
      const updatedPWD = properties.filter(item => item._id !== row?._id);
      setProperties(updatedPWD);
      dispatch(listPWD(wellsData?.row?._id));
    } else {
      dispatch(deletePWDRow(row?._id, wellsData?.row?._id));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const checkMDandPressure = (row, properties) => {
    if (row.md <= 0) {
      return showError(`Measured Depth must be greater than 0.0 ${pwd?.units?.md?.label}`, row._id);
    }

    if (row.p <= 0) {
      return showError(`Pressure must be greater than 0.0 ${pwd?.units?.p?.label}`, row._id);
    }

    const duplicate = properties.find((item) => item._id !== row._id && item.md === row.md);
    if (duplicate) {
      return showError('Repeated Measured depths are not allowed', row._id);
    }

    return true;
  };

  const checkwellsDataValues = (row) => {
    const invalidFields = [];

    if (row?.md === undefined || row?.md === null || row?.md === '') invalidFields.push('Measured Depth');
    if (row?.p === undefined || row?.p === null || row?.p === '') invalidFields.push('Pressure');

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));

      for (const key in row) {
        if (typeof row[key] === 'string' && !isNaN(row[key])) {
          row[key] = Number(row[key]);
        }
      }

      return checkMDandPressure(row, properties);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const savePWD = (row) => {
    if (checkwellsDataValues(row)) {
      const keysToExtract = ['md', 'p'];

      const extracted = Object.fromEntries(
        Object.entries(row).filter(([key]) => keysToExtract.includes(key))
      );

      const converted = unit !== UNIT_SYSTEM.US
        ? {
            md: convertUnitSystem(extracted.md, pwd?.units?.md.conversionFactor),
            p: convertUnitSystem(extracted.p, pwd?.units?.p.conversionFactor),
          }
        : null;

      const payload = unit !== UNIT_SYSTEM.US ? converted : extracted;

      if (row?.isNewRow) {
        dispatch(createPWD(payload, wellsData?.row?._id));
      } else {
        dispatch(updatePWD(payload, wellsData?.row?._id, row?._id));
      }
    }
  };

  const handleOpenUploadModal = () => {
    setUploadOpen(true);
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    setProperties([]);
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            {wellsData?.row?.name || ''} | PWD <CreatedByInfoDialog data={pwd?.createdDetails} loading={pwd?.loading || disableAction} />
          </Grid>
          <Grid item>
          {isHydraulicsAdmin
            ? (
              <Tooltip title={TOOLTIPS.viewPermissions}>
                <span>
                  <IconButton
                    variant="solid"
                    key="permissions"
                    disabled={pwd?.loading || disableAction || properties?.length === 0}
                    sx={{ color: pwd?.permissionId && 'green' }}
                    onClick={() => {
                      setPermissions({
                        open: true,
                        data: {
                          ...wellsData?.row,
                          _id: pwd?._id,
                          permissionId: pwd?.permissionId
                        }
                      });
                    }}
                  >
                    <PersonAddAlt fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
              )
            : null}
            <IconButton
              onClick={closeHandler}
            >
              <CancelPresentationRounded fontSize='large' />
            </IconButton>
          </Grid>
        </Grid>
    );
  };
  const renderActions = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || pwd?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, pwd?.permissions))}
          onClick={() => {
            savePWD(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={pwd?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, pwd?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: Number(value),
            isDirty,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const handleUploadSuccess = () => {
    setUploadOpen(false);
  };

  const handleUploadCsv = (selectedFile) => {
    if (selectedFile.size >= HYDRAULICS_MAX_FILE_SIZE.ONE_MB_BYTE) {
      dispatch(
        enqueueSnackbar(
          'File must be less than 1 MB ',
          'error',
          new Date().getTime() + Math.random()
        )
      );
    } else {
      dispatch(importWellsPWDCSVFIle(selectedFile, wellsData?.row?._id, setIsLoading, handleUploadSuccess));
    }
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  if (apiRef.current === null) apiRef.current = {};
  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Box className={classes.customTool} sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, pwd?.permissions) && 'none !important' }}>
          <Box>
          <Typography variant='button' fontWeight='700' className={pwd.loading || disableAction ? classes.disableIcon : ''} onClick={handleAddPWD}>+ Add</Typography>
          </Box>
          <Box>
          <Typography
            variant="body1"
            fontWeight="500"
            sx={{ marginLeft: '8px', marginRight: '8px', fontSize: '17px' }}
          >
            |
          </Typography>
          </Box>
          <Box>
          <label className={pwd.loading || disableAction ? classes.disableImportLabel : classes.importLabel} onClick={() => {
            handleOpenUploadModal();
          }}>Import</label>
          </Box>
        </Box>
        <Grid item xs={12}>
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={removePWD}
            handleClose={setDeleteAction}
            deleteRow={deleteRow}
          />
        }
        <UnsavedChangesModal />
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '1.2rem',
                fontWeight: '900 !important',
              },
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            loading={pwd.loading}
            columns={columns(renderCell, renderActions, nonEditableFields, renderValueCell, pwd.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            autoheight
            height={'50vh'}
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            onRowSelectionModelChange={(ids) => {
              const editableField = apiRef?.current.getSelectedRows().get(ids[0])?.isNewRow || false;
              setNonEditableFields(editableField);
            }}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={pwd?.permissions}
          />
        </Grid>
        {uploadOpen && (
          <FileUploadDialog
          open={uploadOpen}
          handleClose={() => {
            setUploadOpen(false);
          }}
          isLoading={isLoading}
          handleUpload={handleUploadCsv}
          fileSize={HYDRAULICS_MAX_FILE_SIZE.ONE_MB}
        />
        )}
        {permissions?.open
          ? (
            <PermissionsModal
              isOpen={permissions?.open}
              data={
                {
                  ...permissions?.data,
                  collection: PERMISSION_DIALOG_SOURCES?.wellsPwd,
                }
              }
              onCloseClick={() => {
                setPermissions({
                  open: false,
                  data: {}
                });
              }}
              refreshParentDialog={() => {
                dispatch(listPWD(wellsData?.row?._id));
              }}
            />
            )
          : null}
      </Grid>
    </ModalDialog>
  );
};
PWDModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  wellsData: PropTypes.object,
  isOpen: PropTypes.bool.isRequired
};
PWDModal.defaultProps = {
  wellsData: {}
};
export default PWDModal;
